/**
 * Financial Executive Summary - Board Level Premium Design
 * Cockpit Executivo Financeiro - Mesmo nível da página Operacional
 * 
 * Adapta-se a:
 * - year=2026: Execução Orçamental | 1T 2026 (vs Orç. 1T26, vs 1T25)
 * - year=2025: Resumo Executivo Financeiro | 2025 (vs Orç. 2025, vs 2024)
 */

import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { 
  TrendingUp, TrendingDown, DollarSign, Wallet, PiggyBank, 
  BarChart3, Target, AlertTriangle, CheckCircle, Activity,
  ArrowUpRight, ArrowDownRight, ChevronDown, ChevronUp,
  Gem, Factory, Gauge, Users, Eye, ShieldCheck, Banknote,
  CircleDollarSign, Receipt, Scale, Percent, Building2, Landmark
} from 'lucide-react';
import {
  Tooltip as ShadcnTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';

// Import REAL financial data
import { 
  realIncomeStatementData,
  toMillionsUSD 
} from '../../data/mock/realFinancialData';

// Navigation mapping for FlowNodes (year-dependent)
const getNavigationMap = (year) => ({
  'revenue': year === 2026 ? 'revenue-detail-2026' : 'revenue-detail',
  'costs': year === 2026 ? 'ebitda-detail-2026' : 'ebitda-detail',
  'liquidity': year === 2026 ? 'liquidity-2026' : 'liquidity',
  'risk': year === 2026 ? 'debt-2026' : 'debt', // Risco e Entrega → Debt
  'debt': year === 2026 ? 'debt-2026' : 'debt',
  'capital': year === 2026 ? 'balance-sheet-2026' : 'balance-sheet',
});

// ============ PREMIUM DESIGN TOKENS ============
const colors = {
  text: {
    primary: '#0E1633',
    secondary: '#425070',
    tertiary: '#7A859D',
    inverse: '#FFFFFF',
  },
  bg: {
    page: '#F5F7FB',
    card: '#FFFFFF',
    dark: '#0D1538',
    header: '#0D1538',
    surface: '#FFFFFF',
    surfaceAlt: '#F8FAFC',
  },
  border: {
    default: '#E3E8F1',
    soft: '#EEF2F7',
  },
  status: {
    positive: '#0C8A4B',
    positiveBg: '#EAF7EF',
    positiveBorder: '#A7F3D0',
    warning: '#A07A18',
    warningBg: '#F8F1DD',
    warningBorder: '#FDE68A',
    negative: '#C33D52',
    negativeBg: '#FBECEF',
    negativeBorder: '#FECACA',
  },
  accent: {
    gold: '#B89A3C',
    goldBg: '#FBF8F0',
  },
  pillars: {
    profitability: '#10B981',
    cash: '#3B82F6',
    costs: '#8B5CF6',
    market: '#F59E0B',
  }
};

const typography = {
  pageTitle: 'font-serif text-[32px] leading-[40px] font-bold tracking-[-0.3px]',
  sectionTitle: 'font-serif text-[24px] leading-[32px] font-semibold tracking-[-0.2px]',
  pillarTitle: 'font-sans text-[11px] leading-[14px] font-bold tracking-[0.5px] uppercase',
  kpiValue: 'font-sans text-[28px] leading-[32px] font-bold tracking-[-0.5px]',
  kpiLabel: 'font-sans text-[12px] leading-[16px] font-semibold tracking-[0.3px]',
  bodyText: 'font-sans text-[14px] leading-[22px] font-normal',
  smallText: 'font-sans text-[12px] leading-[16px] font-normal',
  microText: 'font-sans text-[10px] leading-[12px] font-medium tracking-[0.2px] uppercase',
};

// ============ FINANCIAL DATA ============
const financialData = {
  2026: {
    period: { pt: '1T 2026', en: 'Q1 2026' },
    periodShort: '1T26',
    comparePeriod: { pt: '1T25', en: 'Q1 25' },
    compareLabel: { pt: 'vs 1T25', en: 'vs Q1 25' },
    budgetLabel: { pt: 'vs Orç. 1T26', en: 'vs Bud. Q1 26' },
    // SCORECARD DATA - 4 Pillars × 3 KPIs
    pillars: {
      profitability: {
        name: { pt: 'Rentabilidade', en: 'Profitability' },
        question: { pt: 'A operação gera receita e margem acima do orçamentado?', en: 'Is operation generating revenue and margin above budget?' },
        icon: TrendingUp,
        color: colors.pillars.profitability,
        kpis: [
          { id: 'revenue', label: { pt: 'Receita', en: 'Revenue' }, actual: 82.4, budget: 79.2, yoy: 77.1, unit: 'M USD', isPrimary: true, tooltip: { pt: 'Receita total do período', en: 'Total period revenue' } },
          { id: 'ebitda', label: { pt: 'EBITDA', en: 'EBITDA' }, actual: 30.1, budget: 28.5, yoy: 27.2, unit: 'M USD', tooltip: { pt: 'Resultado antes de juros, impostos, depreciação e amortização', en: 'Earnings before interest, taxes, depreciation and amortization' } },
          { id: 'ebitda_margin', label: { pt: 'Margem EBITDA', en: 'EBITDA Margin' }, actual: 36.5, budget: 36.0, yoy: 35.3, unit: '%', tooltip: { pt: 'EBITDA / Receita', en: 'EBITDA / Revenue' } },
        ]
      },
      cash: {
        name: { pt: 'Caixa & Financiamento', en: 'Cash & Financing' },
        question: { pt: 'A conversão em caixa é robusta e a liquidez confortável?', en: 'Is cash conversion robust and liquidity comfortable?' },
        icon: Wallet,
        color: colors.pillars.cash,
        kpis: [
          { id: 'operating_cash_flow', label: { pt: 'FCO', en: 'OCF' }, actual: 24.8, budget: 23.0, yoy: 21.5, unit: 'M USD', isPrimary: true, tooltip: { pt: 'Fluxo de Caixa Operacional', en: 'Operating Cash Flow' } },
          { id: 'cash_available', label: { pt: 'Caixa Disponível', en: 'Cash Available' }, actual: 52.3, budget: 50.8, yoy: 45.2, unit: 'M USD', tooltip: { pt: 'Caixa e equivalentes disponíveis', en: 'Available cash and equivalents' } },
          { id: 'net_debt_ebitda', label: { pt: 'Díd. Líq./EBITDA', en: 'Net Debt/EBITDA' }, actual: 0.26, budget: 0.30, yoy: 0.35, unit: 'x', inverse: true, tooltip: { pt: 'Rácio de alavancagem - menor é melhor', en: 'Leverage ratio - lower is better' } },
        ]
      },
      costs: {
        name: { pt: 'Disciplina de Custos', en: 'Cost Discipline' },
        question: { pt: 'Os custos estão controlados e os rácios a melhorar?', en: 'Are costs controlled and ratios improving?' },
        icon: Receipt,
        color: colors.pillars.costs,
        kpis: [
          { id: 'opex', label: { pt: 'Custos Operacionais', en: 'Operating Costs' }, actual: 48.2, budget: 49.5, yoy: 47.8, unit: 'M USD', inverse: true, isPrimary: true, tooltip: { pt: 'Total de custos operacionais - menor é melhor', en: 'Total operating costs - lower is better' } },
          { id: 'personnel_ratio', label: { pt: 'Pessoal/Receita', en: 'Personnel/Revenue' }, actual: 12.8, budget: 13.5, yoy: 13.2, unit: '%', inverse: true, tooltip: { pt: 'Rácio custos pessoal/receita - menor é melhor', en: 'Personnel costs/revenue ratio - lower is better' } },
          { id: 'ga_ratio', label: { pt: 'G&A/Receita', en: 'G&A/Revenue' }, actual: 4.2, budget: 4.5, yoy: 4.6, unit: '%', inverse: true, tooltip: { pt: 'Rácio gastos gerais e administrativos/receita', en: 'General & administrative/revenue ratio' } },
        ]
      },
      market: {
        name: { pt: 'Mercado & Valor', en: 'Market & Value' },
        question: { pt: 'O preço médio protege valor e captura prémios?', en: 'Is average price protecting value and capturing premiums?' },
        icon: Gem,
        color: colors.pillars.market,
        kpis: [
          { id: 'avg_price_regular', label: { pt: 'Preço Médio Reg.', en: 'Avg Price Regular' }, actual: 336, budget: 320, yoy: 312, unit: 'USD/ct', isPrimary: true, tooltip: { pt: 'Preço médio de venda de diamantes regulares', en: 'Average selling price of regular diamonds' } },
          { id: 'avg_price_special', label: { pt: 'Preço Médio Esp.', en: 'Avg Price Special' }, actual: 2850, budget: 2600, yoy: 2420, unit: 'USD/ct', tooltip: { pt: 'Preço médio de venda de diamantes especiais', en: 'Average selling price of special diamonds' } },
          { id: 'carats_sold', label: { pt: 'Quilates Vendidos', en: 'Carats Sold' }, actual: 42.5, budget: 45.0, yoy: 44.2, unit: 'Kcts', tooltip: { pt: 'Total de quilates vendidos no período', en: 'Total carats sold in period' } },
        ]
      }
    },
    // FLOW STATUS - Hero section chips
    flowStatus: {
      liquidity: { status: 'healthy', value: 92 },
      budgetDiscipline: { status: 'healthy', value: 104 },
      cashConversion: { status: 'healthy', value: 82 },
    },
    // KEY READINGS - Premium Executive Style
    keyReadings: [
      {
        eyebrow: { pt: 'LEITURA 1', en: 'READING 1' },
        title: { pt: 'Valor acima do plano', en: 'Value above plan' },
        insight: { pt: 'Receita +4% e EBITDA +6% face ao orçamento do 1T26, confirmando execução acima do plano com foco em valor.', en: 'Revenue +4% and EBITDA +6% vs Q1 26 budget, confirming above-plan execution with value focus.' },
        anchor: '+4.0%',
        anchorLabel: { pt: 'Receita vs Orç.', en: 'Revenue vs Bud.' },
        type: 'positive',
        weight: 'primary'
      },
      {
        eyebrow: { pt: 'LEITURA 2', en: 'READING 2' },
        title: { pt: 'Margem expandiu com disciplina', en: 'Margin expanded with discipline' },
        insight: { pt: 'Margem EBITDA de 36.5%, +0.5 p.p. vs orçamento, suportada por disciplina de custos e mix comercial favorável.', en: 'EBITDA margin at 36.5%, +0.5 p.p. vs budget, supported by cost discipline and favorable sales mix.' },
        anchor: '36.5%',
        anchorLabel: { pt: 'Margem EBITDA', en: 'EBITDA Margin' },
        type: 'positive',
        weight: 'secondary'
      },
      {
        eyebrow: { pt: 'LEITURA 3', en: 'READING 3' },
        title: { pt: 'Conversão em caixa acima da meta', en: 'Cash conversion above target' },
        insight: { pt: 'FCO de USD 24.8M representa 82% do EBITDA, acima da meta de 80%, com dívida líquida/EBITDA em 0.26x.', en: 'OCF of USD 24.8M represents 82% of EBITDA, above 80% target, with net debt/EBITDA at 0.26x.' },
        anchor: '82%',
        anchorLabel: { pt: 'Conversão FCO', en: 'OCF Conversion' },
        type: 'positive',
        weight: 'secondary'
      }
    ],
    // CRITICAL HYPOTHESES
    criticalHypotheses: [
      {
        question: { pt: 'A receita executada no trimestre reflecte melhoria estrutural ou efeito de timing?', en: 'Does quarterly revenue reflect structural improvement or timing effect?' },
        evidenceTable: [
          { label: { pt: 'Receita', en: 'Revenue' }, actual: 82.4, budget: 79.2, unit: 'M USD', eval: 'GREEN' },
          { label: { pt: 'Preço Médio Regular', en: 'Avg Price Regular' }, actual: 336, budget: 320, unit: 'USD/ct', eval: 'GREEN' },
          { label: { pt: 'Quilates Vendidos', en: 'Carats Sold' }, actual: 42.5, budget: 45.0, unit: 'Kcts', eval: 'AMBER' },
        ],
        executiveReading: { 
          pt: 'Preço médio +5% vs 1T25 sugere melhoria de mix. Volume -6% vs orçamento indica pressão pontual na produção.',
          en: 'Average price +5% vs Q1 25 suggests mix improvement. Volume -6% vs budget indicates production pressure.'
        },
        implication: { pt: 'Monitorizar se ganho de preço compensa desvio de volume no 2T.', en: 'Monitor if price gain offsets volume deviation in Q2.' },
        status: 'attention'
      },
      {
        question: { pt: 'A margem EBITDA está a expandir por eficiência sustentável ou por factores temporários?', en: 'Is EBITDA margin expanding due to sustainable efficiency or temporary factors?' },
        evidenceTable: [
          { label: { pt: 'Margem EBITDA', en: 'EBITDA Margin' }, actual: 36.5, budget: 36.0, unit: '%', eval: 'GREEN' },
          { label: { pt: 'Custos Operacionais', en: 'Operating Costs' }, actual: 48.2, budget: 49.5, unit: 'M USD', eval: 'GREEN', inverse: true },
          { label: { pt: 'Pessoal/Receita', en: 'Personnel/Revenue' }, actual: 12.8, budget: 13.5, unit: '%', eval: 'GREEN', inverse: true },
        ],
        executiveReading: { 
          pt: 'Custos operacionais -3% vs orç. Rácio pessoal/receita melhorou 0.7pp. G&A controlado.',
          en: 'Operating costs -3% vs budget. Personnel/revenue ratio improved 0.7pp. G&A controlled.'
        },
        implication: { pt: 'Ganhos parecem estruturais. Manter disciplina no 2T para consolidar.', en: 'Gains appear structural. Maintain discipline in Q2 to consolidate.' },
        status: 'healthy'
      },
      {
        question: { pt: 'A geração de caixa acompanha a melhoria de EBITDA com disciplina de capital?', en: 'Does cash generation follow EBITDA improvement with capital discipline?' },
        evidenceTable: [
          { label: { pt: 'FCO', en: 'OCF' }, actual: 24.8, budget: 23.0, unit: 'M USD', eval: 'GREEN' },
          { label: { pt: 'Conversão FCO/EBITDA', en: 'OCF/EBITDA Conversion' }, actual: 82, budget: 80, unit: '%', eval: 'GREEN' },
          { label: { pt: 'Dív. Líq./EBITDA', en: 'Net Debt/EBITDA' }, actual: 0.26, budget: 0.30, unit: 'x', eval: 'GREEN', inverse: true },
        ],
        executiveReading: { 
          pt: 'Conversão EBITDA→FCO de 82% (vs 80% target). CAPEX em 24% do orçamento anual (vs 22% target).',
          en: 'EBITDA→OCF conversion at 82% (vs 80% target). CAPEX at 24% of annual budget (vs 22% target).'
        },
        implication: { pt: 'Liquidez confortável. Margem para acelerar investimentos prioritários.', en: 'Comfortable liquidity. Room to accelerate priority investments.' },
        status: 'healthy'
      }
    ],
    // SYNTHESIS
    synthesis: {
      pt: 'O 1T26 confirmou execução acima do plano, com receita e EBITDA superiores ao orçamento, expansão de margem e conversão sólida em caixa. Num contexto de mercado selectivo, a empresa demonstrou capacidade de preservar valor, controlar custos e sustentar robustez financeira.',
      en: 'Q1 26 confirmed above-plan execution, with revenue and EBITDA above budget, margin expansion and solid cash conversion. In a selective market context, the company demonstrated ability to preserve value, control costs and sustain financial robustness.'
    },
    // OUTLOOK
    outlook: {
      focus: { pt: 'Consolidar no 2T a execução acima do plano observada no 1T.', en: 'Consolidate in Q2 the above-plan execution observed in Q1.' },
      priority: { pt: 'Acelerar a conversão do stockpile em receita e proteger a qualidade da margem.', en: 'Accelerate stockpile to revenue conversion and protect margin quality.' },
      risk: { pt: 'Atraso na recuperação de volume com impacto na trajectória de receita e absorção de custos.', en: 'Delay in volume recovery impacting revenue trajectory and cost absorption.' },
      mandate: { pt: 'Fechar o semestre acima do plano em receita e EBITDA, preservando margem e disciplina de caixa.', en: 'Close the half above plan in revenue and EBITDA, preserving margin and cash discipline.' }
    }
  },
  2025: {
    period: { pt: '2025', en: '2025' },
    periodShort: '2025',
    comparePeriod: { pt: '2024', en: '2024' },
    compareLabel: { pt: 'vs 2024', en: 'vs 2024' },
    budgetLabel: { pt: 'vs Orç. 2025', en: 'vs Bud. 2025' },
    pillars: {
      profitability: {
        name: { pt: 'Rentabilidade', en: 'Profitability' },
        question: { pt: 'A operação gera receita e margem acima do orçamentado?', en: 'Is operation generating revenue and margin above budget?' },
        icon: TrendingUp,
        color: colors.pillars.profitability,
        kpis: [
          { id: 'revenue', label: { pt: 'Receita', en: 'Revenue' }, actual: 105.4, budget: 105.4, yoy: 91.1, unit: 'M USD', isPrimary: true, tooltip: { pt: 'Receita total do ano (Dados Reais)', en: 'Total annual revenue (Real Data)' } },
          { id: 'ebitda', label: { pt: 'EBITDA', en: 'EBITDA' }, actual: 32.9, budget: 32.9, yoy: 30.6, unit: 'M USD', tooltip: { pt: 'Resultado antes de juros, impostos, depreciação e amortização', en: 'Earnings before interest, taxes, depreciation and amortization' } },
          { id: 'ebitda_margin', label: { pt: 'Margem EBITDA', en: 'EBITDA Margin' }, actual: 31.2, budget: 31.2, yoy: 33.6, unit: '%', tooltip: { pt: 'EBITDA / Receita', en: 'EBITDA / Revenue' } },
        ]
      },
      cash: {
        name: { pt: 'Caixa & Financiamento', en: 'Cash & Financing' },
        question: { pt: 'A conversão em caixa é robusta e a liquidez confortável?', en: 'Is cash conversion robust and liquidity comfortable?' },
        icon: Wallet,
        color: colors.pillars.cash,
        kpis: [
          { id: 'operating_cash_flow', label: { pt: 'FCO', en: 'OCF' }, actual: 33.9, budget: 33.9, yoy: 21.7, unit: 'M USD', isPrimary: true, tooltip: { pt: 'Fluxo de Caixa Operacional (Dados Reais)', en: 'Operating Cash Flow (Real Data)' } },
          { id: 'cash_available', label: { pt: 'Caixa Disponível', en: 'Cash Available' }, actual: 6.1, budget: 6.1, yoy: 5.2, unit: 'M USD', tooltip: { pt: 'Caixa e equivalentes disponíveis', en: 'Available cash and equivalents' } },
          { id: 'net_debt_ebitda', label: { pt: 'Díd. Líq./EBITDA', en: 'Net Debt/EBITDA' }, actual: 0.42, budget: 0.45, yoy: 0.69, unit: 'x', inverse: true, tooltip: { pt: 'Rácio de alavancagem - menor é melhor', en: 'Leverage ratio - lower is better' } },
        ]
      },
      costs: {
        name: { pt: 'Disciplina de Custos', en: 'Cost Discipline' },
        question: { pt: 'Os custos estão controlados e os rácios a melhorar?', en: 'Are costs controlled and ratios improving?' },
        icon: Receipt,
        color: colors.pillars.costs,
        kpis: [
          { id: 'opex', label: { pt: 'Custos Operacionais', en: 'Operating Costs' }, actual: 72.6, budget: 72.6, yoy: 60.5, unit: 'M USD', inverse: true, isPrimary: true, tooltip: { pt: 'Total de custos operacionais (Dados Reais)', en: 'Total operating costs (Real Data)' } },
          { id: 'personnel_ratio', label: { pt: 'Pessoal/Receita', en: 'Personnel/Revenue' }, actual: 36.0, budget: 36.0, yoy: 34.9, unit: '%', inverse: true, tooltip: { pt: 'Rácio custos pessoal/receita', en: 'Personnel costs/revenue ratio' } },
          { id: 'ga_ratio', label: { pt: 'G&A/Receita', en: 'G&A/Revenue' }, actual: 7.2, budget: 7.2, yoy: 6.9, unit: '%', inverse: true, tooltip: { pt: 'Rácio gastos gerais e administrativos/receita', en: 'General & administrative/revenue ratio' } },
        ]
      },
      market: {
        name: { pt: 'Mercado & Valor', en: 'Market & Value' },
        question: { pt: 'O preço médio protege valor e captura prémios?', en: 'Is average price protecting value and capturing premiums?' },
        icon: Gem,
        color: colors.pillars.market,
        kpis: [
          { id: 'avg_price_regular', label: { pt: 'Preço Médio Reg.', en: 'Avg Price Regular' }, actual: 247, budget: 235, yoy: 215, unit: 'USD/ct', isPrimary: true, tooltip: { pt: 'Preço médio de venda de diamantes regulares', en: 'Average selling price of regular diamonds' } },
          { id: 'avg_price_special', label: { pt: 'Preço Médio Esp.', en: 'Avg Price Special' }, actual: 545, budget: 500, yoy: 453, unit: 'USD/ct', tooltip: { pt: 'Preço médio de venda de diamantes especiais', en: 'Average selling price of special diamonds' } },
          { id: 'carats_sold', label: { pt: 'Quilates Vendidos', en: 'Carats Sold' }, actual: 150.0, budget: 155.0, yoy: 145.0, unit: 'Kcts', tooltip: { pt: 'Total de quilates vendidos no ano', en: 'Total carats sold in year' } },
        ]
      }
    },
    flowStatus: {
      liquidity: { status: 'healthy', value: 92 },
      budgetDiscipline: { status: 'healthy', value: 104 },
      cashConversion: { status: 'healthy', value: 82 },
    },
    // KEY READINGS - Premium Executive Style (REAL DATA)
    keyReadings: [
      {
        eyebrow: { pt: 'LEITURA 1', en: 'READING 1' },
        title: { pt: 'Ano de transição com receita ajustada', en: 'Transition year with adjusted revenue' },
        insight: { pt: 'Receita de 105.4M USD (Orçamento 2025), reflectindo crescimento de +16% face a 2024 (91.1M USD).', en: 'Revenue of 105.4M USD (2025 Budget), reflecting +16% growth vs 2024 (91.1M USD).' },
        anchor: '+16%',
        anchorLabel: { pt: 'Receita vs 2024', en: 'Revenue vs 2024' },
        type: 'positive',
        weight: 'primary'
      },
      {
        eyebrow: { pt: 'LEITURA 2', en: 'READING 2' },
        title: { pt: 'Margem EBITDA resiliente', en: 'Resilient EBITDA margin' },
        insight: { pt: 'Margem EBITDA de 31.2%, EBITDA de 32.9M USD, demonstrando eficiência operacional consistente.', en: 'EBITDA margin at 31.2%, EBITDA of 32.9M USD, demonstrating consistent operational efficiency.' },
        anchor: '31.2%',
        anchorLabel: { pt: 'Margem EBITDA', en: 'EBITDA Margin' },
        type: 'positive',
        weight: 'secondary'
      },
      {
        eyebrow: { pt: 'LEITURA 3', en: 'READING 3' },
        title: { pt: 'Geração de caixa operacional sólida', en: 'Solid operational cash generation' },
        insight: { pt: 'FCO de 33.9M USD com conversão robusta. Caixa disponível de 6.1M USD em linha com plano.', en: 'OCF of 33.9M USD with robust conversion. Available cash of 6.1M USD in line with plan.' },
        anchor: '33.9M',
        anchorLabel: { pt: 'FCO', en: 'OCF' },
        type: 'positive',
        weight: 'secondary'
      }
    ],
    criticalHypotheses: [
      {
        question: { pt: 'A receita do ano reflecte melhoria estrutural do posicionamento comercial?', en: 'Does annual revenue reflect structural improvement in commercial positioning?' },
        evidenceTable: [
          { label: { pt: 'Receita', en: 'Revenue' }, actual: 105.4, budget: 105.4, unit: 'M USD', eval: 'GREEN' },
          { label: { pt: 'Preço Médio Regular', en: 'Avg Price Regular' }, actual: 247, budget: 235, unit: 'USD/ct', eval: 'GREEN' },
          { label: { pt: 'Quilates Vendidos', en: 'Carats Sold' }, actual: 150.0, budget: 155.0, unit: 'Kcts', eval: 'AMBER' },
        ],
        executiveReading: { 
          pt: 'Preço médio +15% vs 2024 e +5% vs orç. Volume abaixo (-3%) mas compensado por preço.',
          en: 'Average price +15% vs 2024 and +5% vs budget. Volume below (-3%) but offset by price.'
        },
        implication: { pt: 'Estratégia de valor sobre volume validada. Manter selectividade em 2026.', en: 'Value over volume strategy validated. Maintain selectivity in 2026.' },
        status: 'healthy'
      },
      {
        question: { pt: 'A margem EBITDA de 31.2% é sustentável ou beneficiou de factores pontuais?', en: 'Is 31.2% EBITDA margin sustainable or did it benefit from one-off factors?' },
        evidenceTable: [
          { label: { pt: 'Margem EBITDA', en: 'EBITDA Margin' }, actual: 31.2, budget: 31.2, unit: '%', eval: 'GREEN' },
          { label: { pt: 'Custos Operacionais', en: 'Operating Costs' }, actual: 72.6, budget: 72.6, unit: 'M USD', eval: 'GREEN', inverse: true },
          { label: { pt: 'G&A/Receita', en: 'G&A/Revenue' }, actual: 7.2, budget: 7.2, unit: '%', eval: 'GREEN', inverse: true },
        ],
        executiveReading: { 
          pt: 'Custos operacionais controlados apesar de pressões inflacionárias. Rácios de eficiência em linha com plano.',
          en: 'Operating costs controlled despite inflationary pressures. Efficiency ratios in line with plan.'
        },
        implication: { pt: 'Ganhos são estruturais. Base sólida para 2026.', en: 'Gains are structural. Solid base for 2026.' },
        status: 'healthy'
      },
      {
        question: { pt: 'A posição de caixa suporta os planos de investimento de 2026?', en: 'Does cash position support 2026 investment plans?' },
        evidenceTable: [
          { label: { pt: 'Caixa Disponível', en: 'Cash Available' }, actual: 6.1, budget: 6.1, unit: 'M USD', eval: 'GREEN' },
          { label: { pt: 'FCO', en: 'OCF' }, actual: 33.9, budget: 33.9, unit: 'M USD', eval: 'GREEN' },
          { label: { pt: 'Dív. Líq./EBITDA', en: 'Net Debt/EBITDA' }, actual: 0.42, budget: 0.45, unit: 'x', eval: 'GREEN', inverse: true },
        ],
        executiveReading: { 
          pt: 'Caixa de 6.1M USD em linha com orçamento. FCO de 33.9M USD (+56% vs 2024). Covenants cumpridos com margem.',
          en: 'Cash at 6.1M USD in line with budget. OCF of 33.9M USD (+56% vs 2024). Covenants met with headroom.'
        },
        implication: { pt: 'Liquidez adequada. Capacidade para financiar CAPEX 2026.', en: 'Adequate liquidity. Capacity to fund 2026 CAPEX.' },
        status: 'healthy'
      }
    ],
    synthesis: {
      pt: 'O ano 2025 é de consolidação: rentabilidade em linha com orçamento, margem EBITDA de 31.2%, geração de caixa robusta. A empresa entra em 2026 com base financeira sólida.',
      en: '2025 is a consolidation year: profitability in line with budget, EBITDA margin at 31.2%, robust cash generation. The company enters 2026 with a solid financial base.'
    },
    outlook: {
      focus: { pt: 'Transformar robustez em crescimento rentável', en: 'Transform robustness into profitable growth' },
      priority: { pt: 'Monetizar stockpile e capturar capacidade industrial', en: 'Monetize stockpile and capture industrial capacity' },
      risk: { pt: 'Volatilidade de mercado em ambiente macro incerto', en: 'Market volatility in uncertain macro environment' },
      mandate: { pt: 'Crescer receita protegendo margem e gerando caixa', en: 'Grow revenue while protecting margin and generating cash' }
    }
  }
};

// ============ HELPER FUNCTIONS ============
const formatValue = (value, unit) => {
  if (value === null || value === undefined) return '-';
  if (unit === '%' || unit === 'x') return value.toFixed(1);
  if (unit === 'M USD') return value.toFixed(1);
  if (unit === 'USD/ct') return value.toLocaleString();
  if (unit === 'Kcts') return value.toFixed(1);
  return value.toFixed(1);
};

const calculateDeviation = (actual, budget) => {
  if (!budget || budget === 0) return 0;
  return ((actual - budget) / Math.abs(budget)) * 100;
};

const getStatusColor = (deviation, inverse = false) => {
  const adjustedDev = inverse ? -deviation : deviation;
  if (adjustedDev >= 0) return colors.status.positive;
  if (adjustedDev >= -5) return colors.status.warning;
  return colors.status.negative;
};

const getStatusBg = (deviation, inverse = false) => {
  const adjustedDev = inverse ? -deviation : deviation;
  if (adjustedDev >= 0) return colors.status.positiveBg;
  if (adjustedDev >= -5) return colors.status.warningBg;
  return colors.status.negativeBg;
};

// ============ COMPONENTS ============

// Status Dot - minimal indicator (same as Operations)
const StatusDot = ({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'healthy':
        return 'bg-emerald-400';
      case 'attention':
        return 'bg-amber-400';
      case 'critical':
        return 'bg-red-400';
      default:
        return 'bg-slate-400';
    }
  };
  
  return (
    <div className={`w-2 h-2 rounded-full ${getStatusColor()} shadow-sm`} />
  );
};

// Flow Node - compact square design (same as Operations)
const FlowNode = ({ icon: Icon, label, sublabel, status, color, onClick, isActive, showArrow = true }) => {
  const colorConfig = {
    emerald: {
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/30',
      text: 'text-emerald-400',
      ring: 'ring-emerald-400/50',
      glow: 'shadow-emerald-500/20',
    },
    blue: {
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/30',
      text: 'text-blue-400',
      ring: 'ring-blue-400/50',
      glow: 'shadow-blue-500/20',
    },
    purple: {
      bg: 'bg-purple-500/10',
      border: 'border-purple-500/30',
      text: 'text-purple-400',
      ring: 'ring-purple-400/50',
      glow: 'shadow-purple-500/20',
    },
    amber: {
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/30',
      text: 'text-amber-400',
      ring: 'ring-amber-400/50',
      glow: 'shadow-amber-500/20',
    },
    gold: {
      bg: 'bg-yellow-600/10',
      border: 'border-yellow-600/30',
      text: 'text-yellow-500',
      ring: 'ring-yellow-500/50',
      glow: 'shadow-yellow-500/20',
    },
  };
  const colors = colorConfig[color] || colorConfig.emerald;
  
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={onClick}
        className={`
          relative flex flex-col items-center p-2 rounded-lg border-2 transition-all duration-300
          ${colors.bg} ${colors.border}
          ${isActive ? `ring-2 ${colors.ring} shadow-lg ${colors.glow}` : 'hover:scale-105'}
          cursor-pointer min-w-[90px]
        `}
      >
        <Icon className={`w-4 h-4 ${colors.text} mb-0.5`} />
        <span className={`text-[9px] font-semibold ${colors.text} text-center leading-tight`}>
          {label}
        </span>
        {sublabel && (
          <span className={`text-[8px] font-medium ${colors.text} opacity-70 text-center leading-tight`}>
            {sublabel}
          </span>
        )}
        
        {/* Status dot - minimal indicator */}
        <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2">
          <StatusDot status={status} />
        </div>
        
        {/* Active indicator pulse */}
        {isActive && (
          <div className="absolute -top-1 -right-1 w-2.5 h-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
          </div>
        )}
      </button>
      
      {showArrow && (
        <div className="flex items-center text-slate-500">
          <ChevronDown className="w-3 h-3 rotate-[-90deg]" />
        </div>
      )}
    </div>
  );
};

// Monitoring Button - compact chip (same as Operations)
const MonitoringButton = ({ icon: Icon, label, status, color, onClick, isActive }) => {
  const colorConfig = {
    purple: {
      bg: 'bg-purple-500/10',
      border: 'border-purple-500/30',
      text: 'text-purple-400',
      ring: 'ring-purple-400/50',
    },
    rose: {
      bg: 'bg-rose-500/10',
      border: 'border-rose-500/30',
      text: 'text-rose-400',
      ring: 'ring-rose-400/50',
    },
    cyan: {
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-500/30',
      text: 'text-cyan-400',
      ring: 'ring-cyan-400/50',
    },
  };
  const colors = colorConfig[color] || colorConfig.purple;
  
  return (
    <button
      onClick={onClick}
      className={`
        relative flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all duration-300
        ${colors.bg} ${colors.border}
        ${isActive ? `ring-1 ${colors.ring}` : 'hover:scale-105'}
        cursor-pointer
      `}
    >
      <Icon className={`w-4 h-4 ${colors.text}`} />
      <span className={`text-[10px] font-medium ${colors.text}`}>
        {label}
      </span>
      <StatusDot status={status} />
    </button>
  );
};

// ============ FINANCIAL STRUCTURE CARD - Dívida/Liquidez blocks (same size as FlowNode) ============
const FinancialStructureCard = ({ icon: Icon, label, sublabel, percentage, status, color, onClick }) => {
  const colorConfig = {
    purple: {
      bg: 'rgba(139, 92, 246, 0.12)',
      border: 'rgba(139, 92, 246, 0.35)',
      text: '#A78BFA',
    },
    rose: {
      bg: 'rgba(244, 63, 94, 0.12)',
      border: 'rgba(244, 63, 94, 0.35)',
      text: '#FB7185',
    },
  };
  const c = colorConfig[color] || colorConfig.purple;
  
  return (
    <button
      onClick={onClick}
      className="relative flex flex-col items-center p-2 rounded-lg border-2 transition-all duration-300 hover:scale-105 cursor-pointer"
      style={{ 
        backgroundColor: c.bg,
        borderColor: c.border,
        minWidth: '90px',
      }}
    >
      {/* Top: Icon + Label */}
      <div className="flex items-center gap-1 mb-0.5">
        <Icon className="w-4 h-4" style={{ color: c.text }} />
        <span className="text-[9px] font-semibold" style={{ color: c.text }}>
          {label}
        </span>
      </div>
      
      {/* Sublabel */}
      <span className="text-[8px] font-medium" style={{ color: c.text, opacity: 0.8 }}>
        {sublabel}
      </span>
      
      {/* Status dot at bottom - same as FlowNode */}
      <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2">
        <StatusDot status={status} />
      </div>
    </button>
  );
};

// ============ PRIMARY KPI CARD - KPI ÂNCORA - Máximo destaque visual (same as Operations) ============
const PrimaryKPICard = ({ kpi, language }) => {
  const rawDeviation = calculateDeviation(kpi.actual, kpi.budget);
  const rawYoY = calculateDeviation(kpi.actual, kpi.yoy);
  
  const isPositiveOutcome = kpi.inverse ? rawDeviation < 0 : rawDeviation > 0;
  const isYoYPositive = kpi.inverse ? rawYoY < 0 : rawYoY > 0;

  return (
    <TooltipProvider>
      <ShadcnTooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div 
            className="px-4 py-5 hover:bg-slate-50/50 transition-colors cursor-help"
            style={{ 
              borderBottom: `1px solid ${colors.border.soft}`,
              background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFD 100%)'
            }}
            data-testid={`fin-kpi-primary-${kpi.id}`}
          >
            {/* KPI Âncora Label - Bold, prominent */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div 
                  className="w-1.5 h-6 rounded-full"
                  style={{ backgroundColor: colors.accent.gold }}
                />
                <p className="text-[13px] font-bold text-slate-800 tracking-wide">
                  {kpi.label[language]}
                </p>
              </div>
              {/* Trend Circle - Larger, with shadow */}
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center shadow-md"
                style={{ 
                  backgroundColor: isPositiveOutcome ? colors.status.positiveBg : colors.status.negativeBg,
                  border: `2px solid ${isPositiveOutcome ? colors.status.positiveBorder : colors.status.negativeBorder}`
                }}
              >
                {isPositiveOutcome ? (
                  <ArrowUpRight className="w-4 h-4" style={{ color: colors.status.positive }} />
                ) : (
                  <ArrowDownRight className="w-4 h-4" style={{ color: colors.status.negative }} />
                )}
              </div>
            </div>
            
            {/* Large Value Display - Bigger, bolder */}
            <div className="flex items-baseline gap-1 mb-3">
              <span 
                className="text-3xl font-extrabold tracking-tight"
                style={{ color: colors.text.primary }}
              >
                {formatValue(kpi.actual, kpi.unit)}
              </span>
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                {kpi.unit}
              </span>
            </div>
            
            {/* Variance Badges - Inline side by side, compact fit */}
            <div className="flex items-center gap-1.5">
              {/* vs Budget */}
              <div 
                className="flex items-center gap-1 px-1.5 py-0.5 rounded"
                style={{ 
                  backgroundColor: isPositiveOutcome ? colors.status.positiveBg : colors.status.negativeBg,
                }}
              >
                {isPositiveOutcome ? (
                  <TrendingUp className="w-3 h-3" style={{ color: colors.status.positive }} />
                ) : (
                  <TrendingDown className="w-3 h-3" style={{ color: colors.status.negative }} />
                )}
                <span 
                  className="text-[9px] font-bold"
                  style={{ color: isPositiveOutcome ? colors.status.positive : colors.status.negative }}
                >
                  {rawDeviation >= 0 ? '+' : ''}{rawDeviation.toFixed(1)}%
                </span>
                <span 
                  className="text-[8px] font-semibold"
                  style={{ color: isPositiveOutcome ? colors.status.positive : colors.status.negative }}
                >
                  Orç
                </span>
              </div>
              
              {/* vs YoY */}
              <div 
                className="flex items-center gap-1 px-1.5 py-0.5 rounded"
                style={{ 
                  backgroundColor: isYoYPositive ? colors.status.positiveBg : colors.status.negativeBg,
                }}
              >
                {isYoYPositive ? (
                  <TrendingUp className="w-3 h-3" style={{ color: colors.status.positive }} />
                ) : (
                  <TrendingDown className="w-3 h-3" style={{ color: colors.status.negative }} />
                )}
                <span 
                  className="text-[9px] font-bold"
                  style={{ color: isYoYPositive ? colors.status.positive : colors.status.negative }}
                >
                  {rawYoY >= 0 ? '+' : ''}{rawYoY.toFixed(0)}%
                </span>
                <span 
                  className="text-[8px] font-semibold"
                  style={{ color: isYoYPositive ? colors.status.positive : colors.status.negative }}
                >
                  YoY
                </span>
              </div>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="max-w-xs p-0 overflow-hidden rounded-lg"
          style={{ backgroundColor: colors.bg.dark, border: 'none' }}
        >
          <div className="px-3 py-2">
            <span className="text-sm font-semibold text-white">{kpi.label[language]}</span>
            <p className="text-xs text-slate-400 leading-relaxed mt-1">{kpi.tooltip?.[language] || kpi.label[language]}</p>
            {kpi.inverse && (
              <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {language === 'pt' ? 'Menor é melhor' : 'Lower is better'}
              </p>
            )}
          </div>
        </TooltipContent>
      </ShadcnTooltip>
    </TooltipProvider>
  );
};

// ============ SUPPORT KPI CARD - Same layout as Operations (exact same sizes) ============
const SupportKPICard = ({ kpi, language }) => {
  const rawDeviation = calculateDeviation(kpi.actual, kpi.budget);
  const rawYoY = calculateDeviation(kpi.actual, kpi.yoy);
  
  const isPositiveOutcome = kpi.inverse ? rawDeviation < 0 : rawDeviation > 0;
  const isYoYPositive = kpi.inverse ? rawYoY < 0 : rawYoY > 0;

  return (
    <TooltipProvider>
      <ShadcnTooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div 
            className="px-3 py-2.5 border-b last:border-b-0 hover:bg-white/50 transition-colors cursor-help"
            style={{ borderColor: colors.border.soft }}
            data-testid={`fin-kpi-support-${kpi.id}`}
          >
            {/* Compact Row: Label + Mini Trend - Same as Operations */}
            <div className="flex items-center justify-between mb-1">
              <p className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">
                {kpi.label[language]}
              </p>
              {/* Micro Trend Dot - Same size as Operations */}
              <div 
                className="w-4 h-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: isPositiveOutcome ? colors.status.positiveBg : colors.status.negativeBg }}
              >
                {isPositiveOutcome ? (
                  <ArrowUpRight className="w-2.5 h-2.5" style={{ color: colors.status.positive }} />
                ) : (
                  <ArrowDownRight className="w-2.5 h-2.5" style={{ color: colors.status.negative }} />
                )}
              </div>
            </div>
            
            {/* Value - Same size as Operations (text-lg) */}
            <div className="flex items-baseline gap-1 mb-1.5">
              <span className="text-lg font-bold text-slate-600 tracking-tight">
                {formatValue(kpi.actual, kpi.unit)}
              </span>
              <span className="text-[8px] text-slate-400 font-medium">
                {kpi.unit}
              </span>
            </div>
            
            {/* Variance Badges - Same size as Operations */}
            <div className="flex gap-1.5">
              <span 
                className="text-[8px] font-bold px-1.5 py-0.5 rounded"
                style={{ 
                  color: isPositiveOutcome ? colors.status.positive : colors.status.negative,
                  backgroundColor: isPositiveOutcome ? colors.status.positiveBg : colors.status.negativeBg 
                }}
              >
                {rawDeviation >= 0 ? '+' : ''}{rawDeviation.toFixed(1)}% Orç
              </span>
              <span 
                className="text-[8px] font-bold px-1.5 py-0.5 rounded"
                style={{ 
                  color: isYoYPositive ? colors.status.positive : colors.status.negative,
                  backgroundColor: isYoYPositive ? colors.status.positiveBg : colors.status.negativeBg 
                }}
              >
                {rawYoY >= 0 ? '+' : ''}{rawYoY.toFixed(0)}% YoY
              </span>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="max-w-xs p-0 overflow-hidden rounded-lg"
          style={{ backgroundColor: colors.bg.dark, border: 'none' }}
        >
          <div className="px-3 py-2">
            <span className="text-sm font-semibold text-white">{kpi.label[language]}</span>
            <p className="text-xs text-slate-400 leading-relaxed mt-1">{kpi.tooltip?.[language] || kpi.label[language]}</p>
            {kpi.inverse && (
              <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {language === 'pt' ? 'Menor é melhor' : 'Lower is better'}
              </p>
            )}
          </div>
        </TooltipContent>
      </ShadcnTooltip>
    </TooltipProvider>
  );
};

// ============ PILLAR COMPONENT - Updated for 3 KPIs with Strong Hierarchy (same as Operations) ============
const PillarColumn = ({ pillarData, language }) => {
  const Icon = pillarData.icon;
  const primaryKPI = pillarData.kpis.find(kpi => kpi.isPrimary);
  const supportKPIs = pillarData.kpis.filter(kpi => !kpi.isPrimary);
  
  return (
    <div className="flex flex-col h-full">
      {/* Pillar Header - Premium with stronger titles */}
      <div 
        className="px-3 py-3 rounded-t-lg"
        style={{ 
          backgroundColor: `${pillarData.color}08`,
          borderBottom: `3px solid ${pillarData.color}`,
        }}
      >
        <div className="flex items-center gap-2.5 mb-1.5">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center shadow-sm"
            style={{ backgroundColor: `${pillarData.color}15` }}
          >
            <Icon className="w-4.5 h-4.5" style={{ color: pillarData.color }} />
          </div>
          <span 
            className="text-[12px] font-extrabold tracking-wide uppercase"
            style={{ color: pillarData.color }}
          >
            {pillarData.name[language]}
          </span>
        </div>
        <p className="text-[9px] italic text-slate-500 leading-tight pl-[42px]">
          {pillarData.question[language]}
        </p>
      </div>
      
      {/* KPIs Container */}
      <div 
        className="flex-1 bg-white rounded-b-lg border border-t-0 shadow-sm overflow-hidden"
        style={{ borderColor: colors.border.default }}
      >
        {/* Primary KPI - KPI Âncora - Maximum prominence */}
        {primaryKPI && (
          <PrimaryKPICard kpi={primaryKPI} language={language} />
        )}
        
        {/* Support KPIs - Same style as Operations */}
        <div 
          className="py-1"
          style={{ backgroundColor: '#F8FAFC' }}
        >
          <p className="px-3 py-1 text-[7px] font-semibold uppercase tracking-wider text-slate-400">
            {language === 'pt' ? 'KPIs de Suporte' : 'Support KPIs'}
          </p>
          {supportKPIs.map((kpi) => (
            <SupportKPICard key={kpi.id} kpi={kpi} language={language} />
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ KEY READING CARD - Premium Executive Style ============
const KeyReadingCard = ({ reading, language }) => {
  // Premium color palette
  const premiumColors = {
    bg: '#FAFBF8',           // Off-white / marfim mais claro
    border: '#E5EAE0',       // Border subtil
    textPrimary: '#10205C',  // Texto principal escuro
    textSecondary: '#5B667A', // Texto secundário
    accent: '#0E9F6E',       // Verde premium
  };

  return (
    <div 
      className={`relative rounded-[16px] overflow-hidden transition-all duration-300 hover:shadow-md ${reading.weight === 'primary' ? 'ring-1 ring-emerald-100' : ''}`}
      style={{ 
        backgroundColor: premiumColors.bg,
        border: `1px solid ${premiumColors.border}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
      }}
    >
      {/* Accent line - top border */}
      <div 
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ backgroundColor: premiumColors.accent }}
      />
      
      <div className="px-5 py-5">
        {/* Anchor Number - Dominant + Label */}
        <div className="flex items-start justify-between mb-4">
          <div>
            {/* Executive Title */}
            <h4 
              className="text-[15px] font-bold leading-snug"
              style={{ color: premiumColors.textPrimary }}
            >
              {reading.title[language]}
            </h4>
          </div>
          
          {/* Anchor Number */}
          <div className="text-right flex-shrink-0 ml-3">
            <span 
              className="text-[36px] font-extrabold leading-none tracking-tight"
              style={{ color: premiumColors.accent }}
            >
              {reading.anchor}
            </span>
            <p 
              className="text-[9px] font-semibold mt-1 uppercase tracking-wide"
              style={{ color: premiumColors.textSecondary }}
            >
              {reading.anchorLabel[language]}
            </p>
          </div>
        </div>
        
        {/* Insight Text - Concise */}
        <p 
          className="text-[12px] leading-[1.65]"
          style={{ color: premiumColors.textSecondary }}
        >
          {reading.insight[language]}
        </p>
      </div>
    </div>
  );
};

// ============ KPI STATUS DOT (for Evidence Table) ============
const KPIStatusDot = ({ evalResult }) => {
  const colorMap = {
    GREEN: { bg: '#166534' },
    AMBER: { bg: '#92400E' },
    RED: { bg: '#991B1B' },
  };
  const c = colorMap[evalResult] || colorMap.AMBER;
  
  return (
    <div 
      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
      style={{ backgroundColor: c.bg }}
      title={evalResult}
    />
  );
};

// ============ FINANCIAL EVIDENCE TABLE (Same as Operations) ============
const FinancialEvidenceTable = ({ evidenceData, language }) => {
  const kpiColors = {
    green: { bg: '#DCFCE7', text: '#166534' },
    amber: { bg: '#FEF3C7', text: '#92400E' },
    red: { bg: '#FEE2E2', text: '#991B1B' },
  };

  const getVarianceColor = (evalResult) => {
    if (evalResult === 'GREEN') return kpiColors.green;
    if (evalResult === 'AMBER') return kpiColors.amber;
    return kpiColors.red;
  };

  const formatTableValue = (value, unit) => {
    if (value === null || value === undefined) return '-';
    if (unit === '%' || unit === 'x') return value.toFixed(1);
    if (unit === 'M USD') return value.toFixed(1);
    if (unit === 'USD/ct') return value.toLocaleString();
    if (unit === 'Kcts') return value.toFixed(1);
    return value.toFixed(1);
  };

  const formatTableVariance = (actual, budget, inverse = false) => {
    if (!budget || budget === 0) return '0%';
    const variance = ((actual - budget) / Math.abs(budget)) * 100;
    return `${variance >= 0 ? '+' : ''}${variance.toFixed(1)}%`;
  };

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <div 
        className="grid grid-cols-12 gap-2 px-4 py-2 text-[9px] font-bold uppercase tracking-wider text-slate-500"
        style={{ backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}
      >
        <div className="col-span-5">{language === 'pt' ? 'Indicador' : 'Indicator'}</div>
        <div className="col-span-3 text-right">{language === 'pt' ? 'Valor' : 'Value'}</div>
        <div className="col-span-4 text-right">{language === 'pt' ? 'vs Orç.' : 'vs Budget'}</div>
      </div>
      
      <div className="bg-white">
        {evidenceData.map((kpi, idx) => {
          const varColor = getVarianceColor(kpi.eval);
          
          return (
            <div 
              key={idx}
              className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center"
              style={{ borderBottom: idx < evidenceData.length - 1 ? '1px solid #F1F5F9' : 'none' }}
            >
              <div className="col-span-5 flex items-center gap-2">
                <KPIStatusDot evalResult={kpi.eval} />
                <span className="text-[11px] text-slate-600 font-medium">
                  {kpi.label[language]}
                </span>
              </div>
              <div className="col-span-3 text-right">
                <span className="text-[12px] font-bold text-slate-800">
                  {formatTableValue(kpi.actual, kpi.unit)} <span className="text-[9px] text-slate-400 font-medium">{kpi.unit}</span>
                </span>
              </div>
              <div className="col-span-4 text-right">
                <span 
                  className="inline-flex items-center text-[10px] font-bold px-2 py-1 rounded"
                  style={{ color: varColor.text, backgroundColor: varColor.bg }}
                >
                  {formatTableVariance(kpi.actual, kpi.budget, kpi.inverse)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============ FINANCIAL HYPOTHESIS STATUS BADGE ============
const FinancialStatusBadge = ({ status, language }) => {
  const statusConfig = {
    healthy: { 
      bg: '#DCFCE7', text: '#166534', border: '#86EFAC',
      label: { pt: 'Sob Controlo', en: 'Under Control' }
    },
    attention: { 
      bg: '#FEF3C7', text: '#92400E', border: '#FDE68A',
      label: { pt: 'Com Atenção', en: 'With Attention' }
    },
    critical: { 
      bg: '#FEE2E2', text: '#991B1B', border: '#FECACA',
      label: { pt: 'Crítico', en: 'Critical' }
    },
  };
  const config = statusConfig[status] || statusConfig.attention;
  
  return (
    <span
      className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide"
      style={{
        backgroundColor: config.bg,
        color: config.text,
        border: `1px solid ${config.border}`
      }}
    >
      {config.label[language]}
    </span>
  );
};

// ============ FINANCIAL HYPOTHESIS CARD - Same layout as Operations ============
const FinancialHypothesisCard = ({ 
  cardNumber, 
  hypothesis, 
  language, 
  isExpanded, 
  onToggle 
}) => {
  const romanNumerals = { 1: 'I', 2: 'II', 3: 'III' };
  
  const cardColors = {
    bg: {
      card: '#FFFFFF',
      dark: '#0A192F',
      gold: '#C5A059',
      goldLight: '#FBF8F0',
    },
    text: {
      primary: '#0E1633',
    }
  };
  
  return (
    <div 
      className="bg-white rounded-2xl shadow-lg border border-slate-200/80 overflow-hidden transition-all duration-300 hover:shadow-xl"
      data-testid={`financial-hypothesis-${cardNumber}`}
    >
      {/* Card Header */}
      <div 
        className="px-6 py-6 cursor-pointer"
        onClick={onToggle}
        style={{ 
          background: `linear-gradient(135deg, ${cardColors.bg.goldLight} 0%, #FFFFFF 100%)`,
          borderBottom: `1px solid ${cardColors.bg.gold}20`
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ backgroundColor: cardColors.bg.dark }}
            >
              <span className="text-[11px] font-bold" style={{ color: cardColors.bg.gold }}>
                {romanNumerals[cardNumber]}
              </span>
            </div>
            <FinancialStatusBadge status={hypothesis.status} language={language} />
          </div>
          <button className="p-1.5 rounded-full hover:bg-slate-200/50 transition-colors">
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </button>
        </div>
        
        <h3 
          className="text-[17px] font-semibold leading-relaxed pr-4"
          style={{ color: cardColors.text.primary }}
        >
          {hypothesis.question[language]}
        </h3>
      </div>
      
      {/* Expanded Content - Same layout as Operations */}
      {isExpanded && (
        <div className="px-6 py-5 space-y-5">
          {/* Evidência Principal - Table */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-5 rounded-full" style={{ backgroundColor: cardColors.bg.gold }} />
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {language === 'pt' ? 'Evidência Principal' : 'Key Evidence'}
              </span>
            </div>
            <FinancialEvidenceTable evidenceData={hypothesis.evidenceTable} language={language} />
          </div>
          
          {/* Leitura Executiva */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-5 rounded-full" style={{ backgroundColor: cardColors.bg.gold }} />
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {language === 'pt' ? 'Leitura Executiva' : 'Executive Reading'}
              </span>
            </div>
            <p className="text-[13px] text-slate-700 leading-relaxed pl-4">
              {hypothesis.executiveReading[language]}
            </p>
          </div>
          
          {/* Implicação de Gestão */}
          <div 
            className="rounded-xl p-4"
            style={{ 
              backgroundColor: cardColors.bg.dark,
              boxShadow: '0 4px 12px rgba(10, 25, 47, 0.15)'
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4" style={{ color: cardColors.bg.gold }} />
              <span 
                className="text-[10px] font-bold uppercase tracking-wider"
                style={{ color: cardColors.bg.gold }}
              >
                {language === 'pt' ? 'Implicação de Gestão' : 'Management Implication'}
              </span>
            </div>
            <p className="text-[13px] leading-relaxed text-white/90">
              {hypothesis.implication[language]}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ FINANCIAL HYPOTHESES SECTION - Same layout as Operations ============
const FinancialHypothesesSection = ({ hypotheses, year, language }) => {
  const [expandedCards, setExpandedCards] = useState({ 1: true, 2: true, 3: true });
  
  const periodLabel = year === 2026 ? '1º Trimestre 2026' : '2025';
  
  const toggleCard = (cardNumber) => {
    setExpandedCards(prev => ({ ...prev, [cardNumber]: !prev[cardNumber] }));
  };
  
  const expandAll = () => setExpandedCards({ 1: true, 2: true, 3: true });
  const collapseAll = () => setExpandedCards({ 1: false, 2: false, 3: false });
  
  const sectionColors = {
    bg: {
      dark: '#0A192F',
      gold: '#C5A059',
    },
    text: {
      primary: '#0E1633',
    }
  };

  return (
    <div className="space-y-5" data-testid="financial-hypotheses-section">
      {/* Section Header - Same as Operations */}
      <div className="flex items-end justify-between border-b border-slate-200 pb-4">
        <div>
          <h2 
            className="font-serif text-2xl font-bold tracking-tight flex items-center gap-3"
            style={{ color: sectionColors.text.primary }}
          >
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md"
              style={{ backgroundColor: sectionColors.bg.dark }}
            >
              <ShieldCheck className="w-5 h-5" style={{ color: sectionColors.bg.gold }} />
            </div>
            {language === 'pt' 
              ? `As 3 Hipóteses Críticas Financeiras ${year === 2026 ? 'do' : 'de'} ${periodLabel}`
              : `The 3 Critical Financial Hypotheses ${year === 2026 ? 'for' : 'for'} ${periodLabel}`
            }
          </h2>
        </div>
        
        {/* Expand/Collapse Controls */}
        <div className="flex items-center gap-2 text-[10px]">
          <button 
            onClick={expandAll}
            className="px-2.5 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 font-medium transition-colors"
          >
            {language === 'pt' ? 'Expandir' : 'Expand'}
          </button>
          <button 
            onClick={collapseAll}
            className="px-2.5 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 font-medium transition-colors"
          >
            {language === 'pt' ? 'Fechar' : 'Collapse'}
          </button>
        </div>
      </div>
      
      {/* 3 Cards Grid */}
      <div className="grid grid-cols-3 gap-5">
        {hypotheses.map((hypothesis, idx) => (
          <FinancialHypothesisCard
            key={idx}
            cardNumber={idx + 1}
            hypothesis={hypothesis}
            language={language}
            isExpanded={expandedCards[idx + 1]}
            onToggle={() => toggleCard(idx + 1)}
          />
        ))}
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============
const FinancialExecutiveSummary = ({ year = 2026, flowMap, setActiveSection }) => {
  const { language } = useLanguage();
  const data = financialData[year] || financialData[2026];
  const navMap = getNavigationMap(year);
  
  // Navigation handler for flow nodes
  const handleNodeClick = (nodeId) => {
    if (setActiveSection && navMap[nodeId]) {
      setActiveSection(navMap[nodeId]);
    }
  };
  
  // Scroll to synthesis section (for Risco e Entrega)
  const scrollToSynthesis = () => {
    const element = document.getElementById('sintese-outlook');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  // Navigate to Balance Sheet and scroll to Estrutura Financeira e Solidez
  const navigateToCapitalStructure = () => {
    if (setActiveSection) {
      const targetSection = year === 2026 ? 'balance-sheet-2026' : 'balance-sheet';
      setActiveSection(targetSection);
      // Use setTimeout to allow navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById('estrutura-financeira-solidez');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  };

  // Page data for PDF export - comprehensive financial summary
  const pageDataForExport = {
    scorecards: [
      { 
        label: language === 'pt' ? 'Receita' : 'Revenue', 
        value: `${data.revenue?.actual || 'N/A'}M USD`,
        vsBudget: data.revenue?.vsBudget,
        vsPrior: data.revenue?.vsPrior
      },
      { 
        label: 'EBITDA', 
        value: `${data.ebitda?.actual || 'N/A'}M USD`,
        vsBudget: data.ebitda?.vsBudget,
        vsPrior: data.ebitda?.vsPrior
      },
      { 
        label: language === 'pt' ? 'Margem EBITDA' : 'EBITDA Margin', 
        value: `${data.ebitdaMargin?.actual || 'N/A'}%`,
        vsBudget: data.ebitdaMargin?.vsBudget,
        vsPrior: data.ebitdaMargin?.vsPrior
      },
      { 
        label: 'OPEX', 
        value: `${data.opex?.actual || 'N/A'}M USD`,
        vsBudget: data.opex?.vsBudget
      },
      { 
        label: language === 'pt' ? 'Resultado Líquido' : 'Net Income', 
        value: `${data.netIncome?.actual || 'N/A'}M USD`,
        vsBudget: data.netIncome?.vsBudget
      }
    ],
    keyMessages: [
      language === 'pt' 
        ? `Receita ${data.revenue?.vsBudget > 0 ? 'acima' : 'abaixo'} do orçamento em ${Math.abs(data.revenue?.vsBudget || 0).toFixed(1)}%`
        : `Revenue ${data.revenue?.vsBudget > 0 ? 'above' : 'below'} budget by ${Math.abs(data.revenue?.vsBudget || 0).toFixed(1)}%`,
      language === 'pt'
        ? `EBITDA de ${data.ebitda?.actual || 'N/A'}M USD com margem de ${data.ebitdaMargin?.actual || 'N/A'}%`
        : `EBITDA of ${data.ebitda?.actual || 'N/A'}M USD with ${data.ebitdaMargin?.actual || 'N/A'}% margin`,
      language === 'pt'
        ? `Custos operacionais ${data.opex?.vsBudget > 0 ? 'acima' : 'abaixo'} do plano em ${Math.abs(data.opex?.vsBudget || 0).toFixed(1)}%`
        : `Operating costs ${data.opex?.vsBudget > 0 ? 'above' : 'below'} plan by ${Math.abs(data.opex?.vsBudget || 0).toFixed(1)}%`
    ]
  };

  return (
    <div className="space-y-8" style={{ backgroundColor: colors.bg.page }} data-testid="financial-executive-summary">
      
      {/* ============ HEADER ============ */}
      <div className="mb-2">
        <div className="flex items-start justify-between">
          <div>
            <h1 className={typography.pageTitle} style={{ color: colors.text.primary }}>
              {year === 2026 
                ? (language === 'pt' ? 'Execução Orçamental' : 'Budget Execution')
                : (language === 'pt' ? 'Resumo Executivo Financeiro' : 'Financial Executive Summary')
              }
              <span style={{ color: colors.accent.gold }}> | {data.period[language]}</span>
            </h1>
            <p className="text-[14px] mt-2" style={{ color: colors.text.secondary }}>
              {data.budgetLabel[language]} {language === 'pt' ? 'e comparativo com' : 'and comparison with'} {data.comparePeriod[language]}
            </p>
          </div>
          
        </div>
      </div>

      {/* ============ HERO SECTION - FINANCIAL FLOW (Same layout as Operations) ============ */}
      <div 
        className="bg-gradient-to-r from-[#0A192F] via-[#112240] to-[#0A192F] rounded-2xl p-4 shadow-lg"
        data-testid="financial-flow-map"
      >
        {/* Header - Clean (same as Operations) */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 bg-[#C5A059] rounded-full"></div>
            <h3 className="text-[#C5A059] font-semibold text-xs uppercase tracking-wider">
              {language === 'pt' ? 'Motor da Execução Financeira' : 'Financial Execution Engine'}
            </h3>
            <span className="px-2 py-0.5 bg-[#C5A059]/20 text-[#C5A059] text-[10px] font-semibold rounded">
              {data.period[language]}
            </span>
          </div>
          
          {/* Legend - Minimal */}
          <div className="flex items-center gap-3 text-[9px] text-slate-500">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-emerald-400" />
              <span>{language === 'pt' ? 'Em linha' : 'On track'}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-amber-400" />
              <span>{language === 'pt' ? 'Atenção' : 'Attention'}</span>
            </div>
          </div>
        </div>

        {/* Main Flow - Clean chain (4 nodes like Operations) */}
        <div className="flex items-center justify-center gap-1 mb-3">
          <FlowNode
            icon={DollarSign}
            label={language === 'pt' ? 'Receita e Mercado' : 'Revenue & Market'}
            status="healthy"
            color="purple"
            showArrow={true}
            onClick={() => handleNodeClick('revenue')}
          />
          <FlowNode
            icon={Receipt}
            label={language === 'pt' ? 'Custos e Margem' : 'Costs & Margin'}
            status="healthy"
            color="emerald"
            showArrow={true}
            onClick={() => handleNodeClick('costs')}
          />
          <FlowNode
            icon={Wallet}
            label={language === 'pt' ? 'Caixa e Liquidez' : 'Cash & Liquidity'}
            status="healthy"
            color="gold"
            showArrow={true}
            onClick={() => handleNodeClick('liquidity')}
          />
          <FlowNode
            icon={Target}
            label={language === 'pt' ? 'Risco e Entrega' : 'Risk & Delivery'}
            status="attention"
            color="amber"
            showArrow={false}
            onClick={scrollToSynthesis}
          />
        </div>

        {/* Estrutura Financeira Row - Replace Monitoring */}
        <div className="flex items-center justify-center gap-4 pt-3 border-t border-white/10">
          <span className="text-slate-400 text-[10px] uppercase tracking-wider font-medium">
            {language === 'pt' ? 'Estrutura Financeira' : 'Financial Structure'}:
          </span>
          <FinancialStructureCard
            icon={Landmark}
            label={language === 'pt' ? 'Dívida' : 'Debt'}
            sublabel={language === 'pt' ? 'Financiamento' : 'Financing'}
            status="healthy"
            color="purple"
            onClick={() => handleNodeClick('debt')}
          />
          <FinancialStructureCard
            icon={Banknote}
            label={language === 'pt' ? 'Estrutura' : 'Capital'}
            sublabel={language === 'pt' ? 'de Capital' : 'Structure'}
            status="healthy"
            color="rose"
            onClick={navigateToCapitalStructure}
          />
        </div>
      </div>

      {/* ============ SCORECARD FINANCEIRO EXECUTIVO ============ */}
      <div className="space-y-4" data-testid="financial-executive-scorecard">
        {/* Header - Premium Executive Style (same as Operations) */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-serif text-xl font-semibold text-primary flex items-center gap-2">
              <Activity className="w-5 h-5" style={{ color: colors.accent.gold }} />
              {language === 'pt' ? 'Scorecard Financeiro Executivo' : 'Executive Financial Scorecard'}
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {language === 'pt' 
                ? `4 pilares × 3 KPIs | ${data.period[language]} vs ${year === 2026 ? 'Orç. 1T26' : 'Orç. 2025'} e vs ${data.comparePeriod[language]}`
                : `4 pillars × 3 KPIs | ${data.period[language]} vs ${year === 2026 ? 'Bud. Q1 26' : 'Bud. 2025'} and vs ${data.comparePeriod[language]}`
              }
            </p>
          </div>
          
          {/* Legend - Minimal */}
          <div className="flex items-center gap-4 text-[10px]">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors.status.positive }} />
              <span className="text-slate-500 font-medium">{language === 'pt' ? 'Favorável' : 'Favorable'}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors.status.negative }} />
              <span className="text-slate-500 font-medium">{language === 'pt' ? 'Atenção' : 'Attention'}</span>
            </div>
          </div>
        </div>
        
        {/* 4 Pillars Grid - Balanced spacing */}
        <div className="grid grid-cols-4 gap-3">
          {Object.values(data.pillars).map((pillar, idx) => (
            <PillarColumn key={idx} pillarData={pillar} language={language} />
          ))}
        </div>
      </div>

      {/* ============ LEITURAS-CHAVE DO TRIMESTRE - Premium Executive Style ============ */}
      <div className="space-y-6">
        {/* Section Header - Premium */}
        <div className="flex items-center gap-3">
          <div 
            className="w-1 h-6 rounded-full"
            style={{ backgroundColor: colors.accent.gold }}
          />
          <h2 className="font-serif text-xl font-semibold" style={{ color: colors.text.primary }}>
            {language === 'pt' ? 'Leituras-Chave do Trimestre' : 'Key Quarterly Readings'}
          </h2>
        </div>
        
        {/* 3 Reading Cards Grid - Premium spacing */}
        <div className="grid grid-cols-3 gap-5">
          {data.keyReadings.map((reading, idx) => (
            <KeyReadingCard key={idx} reading={reading} language={language} index={idx} />
          ))}
        </div>
      </div>

      {/* ============ HIPÓTESES CRÍTICAS FINANCEIRAS - Same layout as Operations ============ */}
      <FinancialHypothesesSection 
        hypotheses={data.criticalHypotheses} 
        year={year} 
        language={language} 
      />

      {/* ============ SÍNTESE E OUTLOOK — FECHO EXECUTIVO PREMIUM ============ */}
      <div 
        id="sintese-outlook"
        className="grid grid-cols-2 gap-6" 
        data-testid="financial-synthesis-outlook"
      >
        
        {/* BLOCO ESQUERDO — Síntese Institucional Compacta */}
        <div 
          className="flex flex-col"
          style={{ 
            backgroundColor: colors.bg.dark,
            borderRadius: '16px',
            padding: '28px 32px',
            boxShadow: '0 4px 20px rgba(13, 21, 56, 0.12)',
          }}
        >
          {/* Header compacto */}
          <div 
            className="flex items-center"
            style={{ gap: '12px', marginBottom: '20px' }}
          >
            <div 
              className="flex items-center justify-center"
              style={{ 
                width: '22px',
                height: '22px',
                borderRadius: '5px',
                backgroundColor: 'rgba(255,255,255,0.06)'
              }}
            >
              <Users className="w-3 h-3 text-white/50" />
            </div>
            <h3 
              style={{ 
                fontSize: '12px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                color: 'rgba(255,255,255,0.45)',
                textTransform: 'uppercase',
                margin: 0,
              }}
            >
              {language === 'pt' 
                ? `Síntese ${year === 2026 ? 'do 1T26' : 'de 2025'} à Gerência`
                : `${year === 2026 ? 'Q1 26' : '2025'} Summary to Management`
              }
            </h3>
          </div>
          
          {/* Pontos-chave executivos - fácil scan */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div 
                className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                style={{ backgroundColor: '#4ADE80' }}
              />
              <p style={{ fontSize: '14px', lineHeight: '1.5', color: 'rgba(255,255,255,0.85)', margin: 0 }}>
                {language === 'pt' 
                  ? 'Execução acima do plano em receita e EBITDA'
                  : 'Above-plan execution in revenue and EBITDA'
                }
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div 
                className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                style={{ backgroundColor: '#4ADE80' }}
              />
              <p style={{ fontSize: '14px', lineHeight: '1.5', color: 'rgba(255,255,255,0.85)', margin: 0 }}>
                {language === 'pt' 
                  ? 'Expansão de margem com conversão sólida em caixa'
                  : 'Margin expansion with solid cash conversion'
                }
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div 
                className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                style={{ backgroundColor: '#4ADE80' }}
              />
              <p style={{ fontSize: '14px', lineHeight: '1.5', color: 'rgba(255,255,255,0.85)', margin: 0 }}>
                {language === 'pt' 
                  ? 'Capacidade de preservar valor em mercado selectivo'
                  : 'Ability to preserve value in selective market'
                }
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div 
                className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                style={{ backgroundColor: '#4ADE80' }}
              />
              <p style={{ fontSize: '14px', lineHeight: '1.5', color: 'rgba(255,255,255,0.85)', margin: 0 }}>
                {language === 'pt' 
                  ? 'Disciplina de custos e robustez financeira sustentada'
                  : 'Cost discipline and sustained financial robustness'
                }
              </p>
            </div>
          </div>
        </div>

        {/* BLOCO DIREITO — Outlook Executivo Compacto */}
        <div 
          className="flex flex-col"
          style={{ 
            backgroundColor: colors.accent.goldBg, 
            border: `1px solid rgba(184, 154, 60, 0.18)`,
            borderRadius: '16px',
            padding: '28px 32px',
            boxShadow: '0 2px 12px rgba(184, 154, 60, 0.06)',
          }}
        >
          {/* Header compacto */}
          <div 
            className="flex items-center"
            style={{ gap: '12px', marginBottom: '20px' }}
          >
            <div 
              className="flex items-center justify-center"
              style={{ 
                width: '22px',
                height: '22px',
                borderRadius: '5px',
                backgroundColor: 'rgba(184, 154, 60, 0.10)'
              }}
            >
              <Eye className="w-3 h-3" style={{ color: colors.accent.gold }} />
            </div>
            <h3 
              style={{ 
                fontSize: '12px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                color: colors.accent.gold,
                textTransform: 'uppercase',
                margin: 0,
              }}
            >
              Outlook {year === 2026 ? '2T26' : '2026'}
            </h3>
          </div>
          
          {/* Sub-blocos compactos */}
          <div className="space-y-4 flex-1">
            {/* Foco */}
            <div>
              <span style={{ display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '0.6px', textTransform: 'uppercase', color: '#8B9AAF', marginBottom: '6px' }}>
                {language === 'pt' ? 'Foco' : 'Focus'}
              </span>
              <p style={{ fontSize: '13px', fontWeight: '400', lineHeight: '1.4', color: '#374151', margin: 0 }}>
                {data.outlook.focus[language]}
              </p>
            </div>
            
            {/* Prioridade */}
            <div>
              <span style={{ display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '0.6px', textTransform: 'uppercase', color: '#8B9AAF', marginBottom: '6px' }}>
                {language === 'pt' ? 'Prioridade' : 'Priority'}
              </span>
              <p style={{ fontSize: '13px', fontWeight: '400', lineHeight: '1.4', color: '#374151', margin: 0 }}>
                {data.outlook.priority[language]}
              </p>
            </div>
            
            {/* Risco Principal */}
            <div>
              <span style={{ display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '0.6px', textTransform: 'uppercase', color: '#8B9AAF', marginBottom: '6px' }}>
                {language === 'pt' ? 'Risco Principal' : 'Main Risk'}
              </span>
              <p style={{ fontSize: '13px', fontWeight: '400', lineHeight: '1.4', color: '#4B5563', margin: 0 }}>
                {data.outlook.risk[language]}
              </p>
            </div>
          </div>
          
          {/* MANDATO — Callout compacto */}
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(184, 154, 60, 0.25)' }}>
            <span style={{ display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '0.6px', textTransform: 'uppercase', color: colors.accent.gold, marginBottom: '10px' }}>
              {language === 'pt' ? 'Mandato de Gestão' : 'Management Mandate'}
            </span>
            <div style={{ backgroundColor: 'rgba(14, 22, 51, 0.04)', borderRadius: '10px', padding: '14px 18px', borderLeft: `3px solid ${colors.accent.gold}` }}>
              <p style={{ fontSize: '14px', fontWeight: '600', lineHeight: '1.4', color: '#0E1633', margin: 0 }}>
                {data.outlook.mandate[language]}
              </p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default FinancialExecutiveSummary;
